import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Wallet, Plus, RefreshCw, CheckCircle2,
  FileText, Eye, TrendingUp, TrendingDown, Equal
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const BILLETES_ARS = [
  { valor: 20000, label: "$20.000" },
  { valor: 10000, label: "$10.000" },
  { valor: 2000, label: "$2.000" },
  { valor: 1000, label: "$1.000" },
  { valor: 500, label: "$500" },
  { valor: 200, label: "$200" },
  { valor: 100, label: "$100" },
];

const BILLETES_USD = [
  { valor: 100, label: "US$100" },
  { valor: 50, label: "US$50" },
  { valor: 20, label: "US$20" },
  { valor: 10, label: "US$10" },
  { valor: 5, label: "US$5" },
  { valor: 1, label: "US$1" },
];

const MONEDAS_ARS = [
  { valor: 100, label: "$100" },
  { valor: 50, label: "$50" },
  { valor: 25, label: "$25" },
  { valor: 10, label: "$10" },
  { valor: 5, label: "$5" },
  { valor: 2, label: "$2" },
  { valor: 1, label: "$1" },
];

const ArqueoCaja = () => {
  const [loading, setLoading] = useState(true);
  const [cuentasCaja, setCuentasCaja] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [arqueos, setArqueos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [arqueoDetalle, setArqueoDetalle] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    cuenta_id: "",
    fecha: new Date().toISOString().split('T')[0],
    observaciones: "",
  });

  const [conteoBilletes, setConteoBilletes] = useState({});
  const [conteoMonedas, setConteoMonedas] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cuentasRes, arqueosRes, monedasRes] = await Promise.all([
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas-tesoreria"),
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/arqueos"),
        axios.get(API_URL + "/api/erp/monedas")
      ]);
      const cajas = cuentasRes.data.filter(c => c.tipo === "CAJA").map(c => {
        const moneda = monedasRes.data.find(m => m.id === c.moneda_id) || monedasRes.data.find(m => m.es_default);
        return { ...c, moneda };
      });
      setCuentasCaja(cajas);
      setMonedas(monedasRes.data);
      setArqueos(arqueosRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCaja = () => {
    return cuentasCaja.find(c => c.id.toString() === form.cuenta_id);
  };

  const getBilletes = () => {
    const caja = getSelectedCaja();
    if (!caja || !caja.moneda) return BILLETES_ARS;
    return caja.moneda.codigo === "USD" ? BILLETES_USD : BILLETES_ARS;
  };

  const getMonedasConteo = () => {
    const caja = getSelectedCaja();
    if (!caja || !caja.moneda) return MONEDAS_ARS;
    return caja.moneda.codigo === "USD" ? [] : MONEDAS_ARS;
  };

  const calcularTotalBilletes = () => {
    return getBilletes().reduce((sum, b) => sum + (b.valor * (parseInt(conteoBilletes[b.valor]) || 0)), 0);
  };

  const calcularTotalMonedas = () => {
    return getMonedasConteo().reduce((sum, m) => sum + (m.valor * (parseInt(conteoMonedas[m.valor]) || 0)), 0);
  };

  const calcularTotalContado = () => {
    return calcularTotalBilletes() + calcularTotalMonedas();
  };

  const getSaldoSistema = () => {
    const cuenta = cuentasCaja.find(c => c.id.toString() === form.cuenta_id);
    return cuenta ? cuenta.saldo_actual : 0;
  };

  const getDiferencia = () => {
    return calcularTotalContado() - getSaldoSistema();
  };

  const handleOpenModal = () => {
    setForm({
      cuenta_id: "",
      fecha: new Date().toISOString().split('T')[0],
      observaciones: "",
    });
    setConteoBilletes({});
    setConteoMonedas({});
    setModalOpen(true);
  };

  const handleCuentaChange = (cuentaId) => {
    setForm({...form, cuenta_id: cuentaId});
    setConteoBilletes({});
    setConteoMonedas({});
  };

  const handleGuardarArqueo = async () => {
    if (!form.cuenta_id) {
      toast.error("Seleccione una cuenta de caja");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cuenta_id: parseInt(form.cuenta_id),
        fecha: form.fecha,
        efectivo_contado: calcularTotalContado(),
        detalle_billetes: conteoBilletes,
        detalle_monedas: conteoMonedas,
        observaciones: form.observaciones,
        generar_ajuste: false
      };

      await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/arqueos", payload);
      toast.success("Arqueo registrado correctamente");
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar arqueo");
    } finally {
      setSaving(false);
    }
  };

  const handleVerDetalle = async (arqueoId) => {
    try {
      const response = await axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/arqueos/" + arqueoId);
      setArqueoDetalle(response.data);
      setDetalleOpen(true);
    } catch (error) {
      toast.error("Error al cargar detalle");
    }
  };

  const formatCurrency = (value, moneda) => {
    const simbolo = moneda?.simbolo || "$";
    return simbolo + " " + new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getEstadoBadge = (estado) => {
    switch(estado) {
      case "OK":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Correcto</Badge>;
      case "SOBRANTE":
        return <Badge className="bg-blue-100 text-blue-700"><TrendingUp className="w-3 h-3 mr-1" />Sobrante</Badge>;
      case "FALTANTE":
        return <Badge className="bg-red-100 text-red-700"><TrendingDown className="w-3 h-3 mr-1" />Faltante</Badge>;
      default:
        return <Badge variant="outline">{estado}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  const diferencia = getDiferencia();
  const selectedCaja = getSelectedCaja();
  const billetes = getBilletes();
  const monedasConteo = getMonedasConteo();

  return (
    <div className="space-y-6" data-testid="arqueo-caja">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Arqueo de Caja</h1>
          <p className="text-gray-600">Control y verificación del efectivo en caja</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handleOpenModal} className="bg-[#2853B9] hover:bg-[#1E3876]">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Arqueo
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {cuentasCaja.map((caja) => (
          <Card key={caja.id} className="cao-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500">{caja.nombre}</p>
                    <Badge variant="outline" className="text-xs">{caja.moneda?.codigo || "ARS"}</Badge>
                  </div>
                  <p className="font-bold text-lg">{formatCurrency(caja.saldo_actual, caja.moneda)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {cuentasCaja.length === 0 && (
          <Card className="cao-card col-span-3">
            <CardContent className="p-8 text-center text-gray-500">
              No hay cuentas de caja configuradas. Cree una cuenta de tipo "Caja" en Tesorería.
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="cao-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Historial de Arqueos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead className="text-right">Saldo Sistema</TableHead>
                <TableHead className="text-right">Efectivo Contado</TableHead>
                <TableHead className="text-right">Diferencia</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {arqueos.map((arqueo) => (
                <TableRow key={arqueo.id}>
                  <TableCell>{arqueo.fecha}</TableCell>
                  <TableCell>{arqueo.cuenta}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(arqueo.saldo_sistema, null)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(arqueo.efectivo_contado, null)}</TableCell>
                  <TableCell className={"text-right font-mono font-semibold " + (arqueo.diferencia > 0 ? 'text-blue-600' : arqueo.diferencia < 0 ? 'text-red-600' : 'text-green-600')}>
                    {formatCurrency(arqueo.diferencia, null)}
                  </TableCell>
                  <TableCell>{getEstadoBadge(arqueo.estado)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleVerDetalle(arqueo.id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {arqueos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No hay arqueos registrados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Arqueo de Caja</DialogTitle>
            <DialogDescription>
              Registre el conteo físico del efectivo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cuenta de Caja</Label>
                <Select value={form.cuenta_id} onValueChange={handleCuentaChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar caja" />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentasCaja.map((caja) => (
                      <SelectItem key={caja.id} value={caja.id.toString()}>
                        {caja.nombre} ({caja.moneda?.codigo || "ARS"}) - {formatCurrency(caja.saldo_actual, caja.moneda)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input 
                  type="date" 
                  value={form.fecha} 
                  onChange={(e) => setForm({...form, fecha: e.target.value})} 
                />
              </div>
            </div>

            {selectedCaja && (
              <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                <Badge>{selectedCaja.moneda?.codigo || "ARS"}</Badge>
                <span className="text-sm text-blue-700">
                  Conteo en {selectedCaja.moneda?.nombre || "Pesos Argentinos"}
                </span>
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-base font-semibold">Conteo de Billetes</Label>
              <div className="grid grid-cols-4 gap-3">
                {billetes.map((b) => (
                  <div key={b.valor} className="flex items-center gap-2">
                    <span className="text-sm w-20">{b.label}</span>
                    <Input
                      type="number"
                      min="0"
                      className="w-20"
                      placeholder="0"
                      value={conteoBilletes[b.valor] || ""}
                      onChange={(e) => setConteoBilletes({...conteoBilletes, [b.valor]: e.target.value})}
                    />
                    <span className="text-xs text-gray-500">
                      = {formatCurrency(b.valor * (parseInt(conteoBilletes[b.valor]) || 0), selectedCaja?.moneda)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-right font-semibold">
                Subtotal Billetes: {formatCurrency(calcularTotalBilletes(), selectedCaja?.moneda)}
              </div>
            </div>

            {monedasConteo.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Conteo de Monedas</Label>
                <div className="grid grid-cols-4 gap-3">
                  {monedasConteo.map((m) => (
                    <div key={m.valor} className="flex items-center gap-2">
                      <span className="text-sm w-12">{m.label}</span>
                      <Input
                        type="number"
                        min="0"
                        className="w-20"
                        placeholder="0"
                        value={conteoMonedas[m.valor] || ""}
                        onChange={(e) => setConteoMonedas({...conteoMonedas, [m.valor]: e.target.value})}
                      />
                    </div>
                  ))}
                </div>
                <div className="text-right font-semibold">
                  Subtotal Monedas: {formatCurrency(calcularTotalMonedas(), selectedCaja?.moneda)}
                </div>
              </div>
            )}

            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Total Efectivo Contado:</span>
                <span className="font-bold text-lg">{formatCurrency(calcularTotalContado(), selectedCaja?.moneda)}</span>
              </div>
              <div className="flex justify-between">
                <span>Saldo según Sistema:</span>
                <span className="font-bold text-lg">{formatCurrency(getSaldoSistema(), selectedCaja?.moneda)}</span>
              </div>
              <div className={"flex justify-between border-t pt-2 " + (diferencia > 0 ? 'text-blue-600' : diferencia < 0 ? 'text-red-600' : 'text-green-600')}>
                <span className="font-semibold">Diferencia:</span>
                <span className="font-bold text-xl flex items-center gap-2">
                  {diferencia > 0 && <TrendingUp className="w-5 h-5" />}
                  {diferencia < 0 && <TrendingDown className="w-5 h-5" />}
                  {diferencia === 0 && <Equal className="w-5 h-5" />}
                  {formatCurrency(diferencia, selectedCaja?.moneda)}
                  {diferencia > 0 && " (Sobrante)"}
                  {diferencia < 0 && " (Faltante)"}
                  {diferencia === 0 && " (OK)"}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Observaciones</Label>
              <Textarea
                value={form.observaciones}
                onChange={(e) => setForm({...form, observaciones: e.target.value})}
                placeholder="Notas adicionales sobre el arqueo..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleGuardarArqueo} 
              disabled={saving || !form.cuenta_id}
              className="bg-[#2853B9] hover:bg-[#1E3876]"
            >
              {saving ? "Guardando..." : "Guardar Arqueo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detalleOpen} onOpenChange={setDetalleOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle del Arqueo</DialogTitle>
          </DialogHeader>
          {arqueoDetalle && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Cuenta</Label>
                  <p className="font-medium">{arqueoDetalle.cuenta_nombre}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Fecha</Label>
                  <p className="font-medium">{arqueoDetalle.fecha}</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span>Saldo Sistema:</span>
                  <span className="font-mono">{formatCurrency(arqueoDetalle.saldo_sistema, null)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Efectivo Contado:</span>
                  <span className="font-mono">{formatCurrency(arqueoDetalle.efectivo_contado, null)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-semibold">Diferencia:</span>
                  <span className={"font-mono font-bold " + (arqueoDetalle.diferencia > 0 ? 'text-blue-600' : arqueoDetalle.diferencia < 0 ? 'text-red-600' : 'text-green-600')}>
                    {formatCurrency(arqueoDetalle.diferencia, null)}
                  </span>
                </div>
              </div>

              {arqueoDetalle.detalle_billetes && Object.keys(arqueoDetalle.detalle_billetes).length > 0 && (
                <div>
                  <Label className="text-sm font-semibold">Detalle Billetes</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2 text-sm">
                    {Object.entries(arqueoDetalle.detalle_billetes).map(([valor, cantidad]) => (
                      cantidad > 0 && (
                        <div key={valor} className="flex justify-between bg-gray-100 p-2 rounded">
                          <span>${valor}</span>
                          <span>x{cantidad}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {arqueoDetalle.observaciones && (
                <div>
                  <Label className="text-xs text-gray-500">Observaciones</Label>
                  <p className="text-sm">{arqueoDetalle.observaciones}</p>
                </div>
              )}

              <div className="pt-2">
                {getEstadoBadge(arqueoDetalle.estado)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalleOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArqueoCaja;
